import { db } from "./dbClient";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";
import jsPDF from "jspdf";
import "jspdf/dist/jspdf.umd.min.js";

const TEST_AREAS = [
  "BSA/AML Program Overall",
  "Customer Due Diligence (CDD)",
  "Enhanced Due Diligence (EDD)",
  "Transaction Monitoring Rules",
  "SAR Filing Process",
  "CTR Filing Process",
  "Sanctions Screening",
  "Customer Onboarding/KYC",
  "PEP Screening",
  "Watchlist Monitoring",
  "Training Program",
  "Recordkeeping",
  "Independent Audit",
  "Model Risk Management",
  "Travel Rule Compliance",
  "Third-Party Due Diligence",
];

const TEST_TYPES = ["Internal Review", "Independent Audit", "Regulatory Exam", "Targeted Testing", "Model Validation", "Sample Review"];
const FINDING_SEVERITY = { critical: "#EF4444", high: "#F59E0B", medium: "#3B82F6", low: "#10B981", informational: "#6B7280" };
const STATUS_COLORS = { planned: "#6B7280", in_progress: "#3B82F6", completed: "#10B981", overdue: "#EF4444" };

function getTranslatedTestType(type, t) {
  const typeMap = {
    "Internal Review": "effectiveness.testTypes.internal_review",
    "Independent Audit": "effectiveness.testTypes.independent_audit",
    "Regulatory Exam": "effectiveness.testTypes.regulatory_exam",
    "Targeted Testing": "effectiveness.testTypes.targeted_testing",
    "Model Validation": "effectiveness.testTypes.model_validation",
    "Sample Review": "effectiveness.testTypes.sample_review",
  };
  return t(typeMap[type] || type);
}

function getTranslatedTestStatus(status, t) {
  const statusMap = {
    "planned": "effectiveness.statuses.planned",
    "in_progress": "effectiveness.statuses.in_progress",
    "completed": "effectiveness.statuses.completed",
    "overdue": "effectiveness.statuses.overdue",
  };
  return t(statusMap[status] || status);
}

function getTranslatedFindingSeverity(severity, t) {
  const severityMap = {
    "critical": "effectiveness.severities.critical",
    "high": "effectiveness.severities.high",
    "medium": "effectiveness.severities.medium",
    "low": "effectiveness.severities.low",
    "informational": "effectiveness.severities.informational",
  };
  return t(severityMap[severity] || severity);
}

const BLANK_TEST = {
  test_area: "",
  test_type: "Internal Review",
  test_name: "",
  tester: "",
  scheduled_date: "",
  scope: "",
  status: "planned",
};

const BLANK_FINDING = {
  title: "",
  description: "",
  severity: "medium",
  recommendation: "",
  remediation_owner: "",
  due_date: "",
  remediation_status: "open",
};

export default function EffectivenessTestingPage({ company, currentUser, team }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);
  const [tests, setTests] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tests");
  const [showTestModal, setShowTestModal] = useState(false);
  const [showFindingModal, setShowFindingModal] = useState(false);
  const [testDraft, setTestDraft] = useState({ ...BLANK_TEST });
  const [findingDraft, setFindingDraft] = useState({ ...BLANK_FINDING });
  const [selectedTest, setSelectedTest] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, [company?.id]);

  useEffect(() => {
    if (showTestModal || showFindingModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showTestModal, showFindingModal]);

  async function load() {
    if (!company?.id) return;
    setLoading(true);
    try {
      const [{ data: t, error: err1 }, { data: f, error: err2 }] = await Promise.all([
        db.from("effectiveness_tests").select("*").eq("company_id", company.id).order("scheduled_date", false),
        db.from("test_findings").select("*").eq("company_id", company.id).order("created_at", false),
      ]);
      setTests(err1 ? [] : (t || []));
      setFindings(err2 ? [] : (f || []));
    } catch (e) {
      setTests([]);
      setFindings([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveTest() {
    if (!testDraft.test_area || !testDraft.test_name || !testDraft.scheduled_date) return;
    setSaving(true);
    try {
      const payload = { ...testDraft, company_id: company.id, created_by: currentUser.email };
      const { error } = await db.from("effectiveness_tests").insert(payload);
      if (!error) {
        setShowTestModal(false);
        setTestDraft({ ...BLANK_TEST });
        await log({
          action: "effectiveness_test_created",
          module: "effectiveness_testing",
          entityId: testDraft.test_name,
          entityName: `${testDraft.test_name} - ${testDraft.test_area}`,
          meta: { testArea: testDraft.test_area, scheduledDate: testDraft.scheduled_date }
        });
        await load();
      }
    } catch (e) {
      console.error('Save test error:', e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveFinding() {
    if (!findingDraft.title || !findingDraft.severity) return;
    setSaving(true);
    try {
      const payload = {
        ...findingDraft,
        company_id: company.id,
        test_id: selectedTest?.id || null,
        test_name: selectedTest?.test_name || null,
        created_by: currentUser.email,
      };
      const { error } = await db.from("test_findings").insert(payload);
      if (!error) {
        setShowFindingModal(false);
        setFindingDraft({ ...BLANK_FINDING });
        await log({
          action: "test_finding_created",
          module: "effectiveness_testing",
          entityId: findingDraft.title,
          entityName: findingDraft.title,
          meta: { severity: findingDraft.severity, testName: selectedTest?.test_name }
        });
        await load();
      }
    } catch (e) {
      console.error('Save finding error:', e.message);
    } finally {
      setSaving(false);
    }
  }

  async function updateTestStatus(id, status) {
    try {
      const { error } = await db.from("effectiveness_tests").update({ status }).eq("id", id);
      if (!error) {
        await load();
      }
    } catch (e) {
      console.error('Update test error:', e.message);
    }
  }

  async function updateFindingStatus(id, remediation_status) {
    try {
      const { error } = await db.from("test_findings").update({ remediation_status }).eq("id", id);
      if (!error) {
        await load();
      }
    } catch (e) {
      console.error('Update finding error:', e.message);
    }
  }

  function exportToPDF() {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 10;
    const lineHeight = 7;
    const margin = 10;
    const maxWidth = pageWidth - 2 * margin;

    // Title
    doc.setFontSize(18);
    doc.text("Effectiveness Testing Report", margin, yPos);
    yPos += 15;
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, yPos);
    yPos += 10;

    // Tests Section
    doc.setFontSize(14);
    doc.text("Scheduled Tests", margin, yPos);
    yPos += 8;
    doc.setFontSize(10);

    tests.forEach((test, idx) => {
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 10;
      }
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, "bold");
      doc.text(`${idx + 1}. ${test.test_name}`, margin, yPos);
      yPos += lineHeight;
      doc.setFont(undefined, "normal");
      doc.text(`Area: ${test.test_area}`, margin + 5, yPos);
      yPos += lineHeight;
      doc.text(`Type: ${test.test_type}`, margin + 5, yPos);
      yPos += lineHeight;
      doc.text(`Scheduled: ${new Date(test.scheduled_date).toLocaleDateString()}`, margin + 5, yPos);
      yPos += lineHeight;
      doc.text(`Status: ${test.status}`, margin + 5, yPos);
      yPos += lineHeight;
      if (test.tester) {
        doc.text(`Tester: ${test.tester}`, margin + 5, yPos);
        yPos += lineHeight;
      }
      yPos += 2;
    });

    yPos += 5;

    // Findings Section
    doc.setFontSize(14);
    doc.text("Test Findings", margin, yPos);
    yPos += 8;
    doc.setFontSize(10);

    findings.forEach((finding, idx) => {
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 10;
      }
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, "bold");
      doc.text(`${idx + 1}. ${finding.title}`, margin, yPos);
      yPos += lineHeight;
      doc.setFont(undefined, "normal");
      doc.text(`Test: ${finding.test_name || "N/A"}`, margin + 5, yPos);
      yPos += lineHeight;
      doc.text(`Severity: ${finding.severity}`, margin + 5, yPos);
      yPos += lineHeight;
      doc.text(`Status: ${finding.remediation_status}`, margin + 5, yPos);
      yPos += lineHeight;
      if (finding.description) {
        const desc = doc.splitTextToSize(`Description: ${finding.description}`, maxWidth - 5);
        desc.forEach(line => {
          doc.text(line, margin + 5, yPos);
          yPos += lineHeight;
        });
      }
      if (finding.remediation_owner) {
        doc.text(`Owner: ${finding.remediation_owner}`, margin + 5, yPos);
        yPos += lineHeight;
      }
      if (finding.due_date) {
        doc.text(`Due: ${new Date(finding.due_date).toLocaleDateString()}`, margin + 5, yPos);
        yPos += lineHeight;
      }
      yPos += 2;
    });

    doc.save(`Effectiveness-Testing-Report-${new Date().toISOString().split('T')[0]}.pdf`);
  }

  const planned = tests.filter(t => t.status === "planned").length;
  const inProgress = tests.filter(t => t.status === "in_progress").length;
  const completed = tests.filter(t => t.status === "completed").length;
  const openFindings = findings.filter(f => f.remediation_status === "open").length;
  const criticalFindings = findings.filter(f => (f.severity === "critical" || f.severity === "high") && f.remediation_status !== "closed").length;

  const F = {
    card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 },
    label: { color: C.textDim, fontSize: 11, letterSpacing: "0.08em", fontFamily: "'DM Mono',monospace", marginBottom: 6, display: "block" },
    input: { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "8px 12px", fontSize: 13, fontFamily: "'DM Mono',monospace", outline: "none", width: "100%", boxSizing: "border-box" },
    btn: (col, disabled) => ({ padding: "8px 18px", background: disabled ? C.border : col, border: "none", borderRadius: 7, color: C.bg, cursor: disabled ? "default" : "pointer", fontSize: 12, fontFamily: "'DM Mono',monospace", fontWeight: 700, opacity: disabled ? 0.7 : 1 }),
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 700, margin: "0 0 4px" }}>{t('effectiveness.title')}</h1>
          <p style={{ color: C.textDim, fontSize: 13, margin: 0 }}>{t('effectiveness.subtitle')}</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={exportToPDF} style={F.btn("#8B5CF6", false)}>⬇ Export PDF</button>
          <button onClick={() => { setSelectedTest(null); setShowFindingModal(true); }} style={F.btn("#F59E0B", false)}>+ Log Finding</button>
          <button onClick={() => setShowTestModal(true)} style={F.btn(C.accent, false)}>+ Schedule Test</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14, marginBottom: 28 }}>
        {[
          { label: "Planned Tests", value: planned, color: STATUS_COLORS.planned },
          { label: "In Progress", value: inProgress, color: STATUS_COLORS.in_progress },
          { label: "Completed", value: completed, color: STATUS_COLORS.completed },
          { label: "Open Findings", value: openFindings, color: "#F59E0B" },
          { label: "Critical/High", value: criticalFindings, color: "#EF4444" },
        ].map(k => (
          <div key={k.label} style={F.card}>
            <div style={{ color: C.textDim, fontSize: 10, marginBottom: 6 }}>{k.label.toUpperCase()}</div>
            <div style={{ color: k.color, fontSize: 26, fontWeight: 700 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 20, marginBottom: 28, borderBottom: `1px solid ${C.border}` }}>
        {["tests", "findings"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "10px 0",
              background: "none",
              border: "none",
              borderBottom: activeTab === tab ? `2px solid ${C.accent}` : "none",
              color: activeTab === tab ? C.text : C.textDim,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: activeTab === tab ? 700 : 400,
            }}
          >
            {tab === "tests" ? "Tests" : "Findings"}
          </button>
        ))}
      </div>

      {/* Tests Tab */}
      {activeTab === "tests" && (
        <div style={{ display: "grid", gap: 12 }}>
          {tests.map(test => (
            <div key={test.id} style={F.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div>
                  <h3 style={{ color: C.text, margin: "0 0 4px", fontSize: 14, fontWeight: 700 }}>{test.test_name}</h3>
                  <p style={{ color: C.textDim, fontSize: 12, margin: "0 0 8px" }}>{test.test_area}</p>
                  <div style={{ display: "flex", gap: 12 }}>
                    <span style={{ background: `${STATUS_COLORS[test.status]}20`, color: STATUS_COLORS[test.status], fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 700, textTransform: "uppercase" }}>
                      {getTranslatedTestStatus(test.status, t)}
                    </span>
                    <span style={{ color: C.textDim, fontSize: 12 }}>Scheduled: {new Date(test.scheduled_date).toLocaleDateString()}</span>
                  </div>
                </div>
                <select
                  value={test.status}
                  onChange={e => updateTestStatus(test.id, e.target.value)}
                  style={{ ...F.input, width: "auto" }}
                >
                  {["planned", "in_progress", "completed", "overdue"].map(s => (
                    <option key={s} value={s}>{getTranslatedTestStatus(s, t)}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Findings Tab */}
      {activeTab === "findings" && (
        <div style={{ display: "grid", gap: 12 }}>
          {findings.map(f => (
            <div key={f.id} style={F.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div>
                  <h3 style={{ color: C.text, margin: "0 0 4px", fontSize: 14, fontWeight: 700 }}>{f.title}</h3>
                  <p style={{ color: C.textDim, fontSize: 12, margin: "0 0 8px" }}>{f.test_name}</p>
                  <div style={{ display: "flex", gap: 12 }}>
                    <span style={{ background: `${FINDING_SEVERITY[f.severity]}20`, color: FINDING_SEVERITY[f.severity], fontSize: 11, padding: "2px 8px", borderRadius: 10, fontWeight: 700, textTransform: "uppercase" }}>
                      {getTranslatedFindingSeverity(f.severity, t)}
                    </span>
                    {f.due_date && <span style={{ color: C.textDim, fontSize: 12 }}>Due: {new Date(f.due_date).toLocaleDateString()}</span>}
                  </div>
                </div>
                <select
                  value={f.remediation_status}
                  onChange={e => updateFindingStatus(f.id, e.target.value)}
                  style={{ ...F.input, width: "auto" }}
                >
                  {["open", "in_progress", "closed"].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Test Modal - Inline */}
      {showTestModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, width: 520, maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ color: C.text, margin: 0, fontSize: 16, fontWeight: 700 }}>Schedule Effectiveness Test</h2>
              <button onClick={() => setShowTestModal(false)} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 24 }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={F.label}>TEST NAME *</label>
                <input value={testDraft.test_name} onChange={e => setTestDraft(d => ({ ...d, test_name: e.target.value }))} placeholder="e.g. Q2 2026 Transaction Monitoring Review" style={F.input} />
              </div>
              <div>
                <label style={F.label}>COMPLIANCE AREA *</label>
                <select value={testDraft.test_area} onChange={e => setTestDraft(d => ({ ...d, test_area: e.target.value }))} style={{ ...F.input }}>
                  <option value="">Select area…</option>
                  {TEST_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={F.label}>TEST TYPE</label>
                  <select value={testDraft.test_type} onChange={e => setTestDraft(d => ({ ...d, test_type: e.target.value }))} style={{ ...F.input }}>
                    {TEST_TYPES.map(tt => <option key={tt} value={tt}>{getTranslatedTestType(tt, t)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={F.label}>SCHEDULED DATE *</label>
                  <input type="date" value={testDraft.scheduled_date} onChange={e => setTestDraft(d => ({ ...d, scheduled_date: e.target.value }))} style={F.input} />
                </div>
              </div>
              <div>
                <label style={F.label}>TESTER / LEAD</label>
                <input value={testDraft.tester} onChange={e => setTestDraft(d => ({ ...d, tester: e.target.value }))} placeholder="Name or team responsible" style={F.input} />
              </div>
              <div>
                <label style={F.label}>TEST SCOPE</label>
                <textarea value={testDraft.scope} onChange={e => setTestDraft(d => ({ ...d, scope: e.target.value }))} placeholder="Describe what will be tested, sample size, methodology…" rows={3} style={{ ...F.input, resize: "vertical" }} />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setShowTestModal(false)} style={{ flex: 1, padding: "10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>Cancel</button>
                <button onClick={handleSaveTest} disabled={saving || !testDraft.test_area || !testDraft.test_name || !testDraft.scheduled_date} style={{ flex: 1, ...F.btn(C.accent, saving || !testDraft.test_area || !testDraft.test_name || !testDraft.scheduled_date) }}>{saving ? "Saving…" : "Schedule Test"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Finding Modal - Inline */}
      {showFindingModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, width: 520, maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ color: C.text, margin: 0, fontSize: 16, fontWeight: 700 }}>Log Finding</h2>
              <button onClick={() => { setShowFindingModal(false); setSelectedTest(null); }} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 24 }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={F.label}>FINDING TITLE *</label>
                <input value={findingDraft.title} onChange={e => setFindingDraft(d => ({ ...d, title: e.target.value }))} placeholder="Brief description of the finding" style={F.input} />
              </div>
              <div>
                <label style={F.label}>SEVERITY *</label>
                <select value={findingDraft.severity} onChange={e => setFindingDraft(d => ({ ...d, severity: e.target.value }))} style={{ ...F.input }}>
                  {Object.keys(FINDING_SEVERITY).map(severity => <option key={severity} value={severity}>{getTranslatedFindingSeverity(severity, t)}</option>)}
                </select>
              </div>
              <div>
                <label style={F.label}>DESCRIPTION</label>
                <textarea value={findingDraft.description} onChange={e => setFindingDraft(d => ({ ...d, description: e.target.value }))} placeholder="Detailed description of the issue found…" rows={3} style={{ ...F.input, resize: "vertical" }} />
              </div>
              <div>
                <label style={F.label}>RECOMMENDATION</label>
                <textarea value={findingDraft.recommendation} onChange={e => setFindingDraft(d => ({ ...d, recommendation: e.target.value }))} placeholder="Recommended corrective action…" rows={2} style={{ ...F.input, resize: "vertical" }} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={F.label}>REMEDIATION OWNER</label>
                  <input value={findingDraft.remediation_owner} onChange={e => setFindingDraft(d => ({ ...d, remediation_owner: e.target.value }))} placeholder="Responsible person/team" style={F.input} />
                </div>
                <div>
                  <label style={F.label}>DUE DATE</label>
                  <input type="date" value={findingDraft.due_date} onChange={e => setFindingDraft(d => ({ ...d, due_date: e.target.value }))} style={F.input} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setShowFindingModal(false); setSelectedTest(null); }} style={{ flex: 1, padding: "10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 13 }}>Cancel</button>
                <button onClick={handleSaveFinding} disabled={saving || !findingDraft.title} style={{ flex: 1, ...F.btn(C.accent, saving || !findingDraft.title) }}>{saving ? "Saving…" : "Log Finding"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
